/**
 * Renders getConnectionStatus()'s output as the row's single status strip —
 * at most one per row, indented under the identity column. Sync-failed rows
 * get a chevron that reveals the raw last_error_message.
 */
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import Icon from "../../theme/untitled/icons/Icon";
import SyncProgressBar from "./SyncProgressBar";
import { openAuthWindow } from "../../consts/pco";
import styles from "./ConnectionRow.module.scss";

const StatusStrip = ({ status, integration }) => {
  const [expanded, setExpanded] = useState(false);

  // Called unconditionally, above the early returns below, per rules of
  // hooks — only actually wired to a button when status.actionType/chevron
  // says so.
  const handleReauth = useCallback(
    () => openAuthWindow(integration.auth_url),
    [integration]
  );
  const handleToggleExpanded = useCallback(() => setExpanded((e) => !e), []);

  if (status.kind === "healthy") {
    return null;
  }

  if (status.kind === "progress") {
    return <SyncProgressBar integration={integration} />;
  }

  const chevronIconName = expanded ? "ChevronUp" : "ChevronDown";

  return (
    <div
      className={classNames(styles.stripWrap, styles[`strip--${status.kind}`])}
    >
      <div className={styles.strip}>
        {status.icon && (
          <Icon name={status.icon} size={18} className={styles.stripIcon} />
        )}
        <span className={styles.stripText}>
          <span className={styles.stripTitle}>{status.title}</span>
          {status.detail && (
            <span className={styles.stripDetail}>{status.detail}</span>
          )}
        </span>
        {status.actionType === "reauth" && (
          <button
            type="button"
            className={styles.stripAction}
            onClick={handleReauth}
          >
            {status.actionLabel}
          </button>
        )}
        {status.actionType === "link" && (
          <Link to={status.actionHref} className={styles.stripAction}>
            {status.actionLabel}
          </Link>
        )}
        {status.chevron && (
          <button
            type="button"
            className={styles.stripChevron}
            onClick={handleToggleExpanded}
            aria-expanded={expanded}
          >
            <Icon name={chevronIconName} size={16} />
          </button>
        )}
      </div>
      {status.chevron && expanded && (
        <div className={styles.stripRaw}>{status.rawDetail}</div>
      )}
    </div>
  );
};

export default StatusStrip;
